// Pure display rules for the office's minisites (OfficeMinisitesPanel). The tags say what the
// viewer's OWN minisite card says for the same facts (MinisiteSubTab), in the same words and
// colours, so a listing reads the same on a colleague's page as on one's own.
import type { REST_schema } from "../../../../../../@types/sharedSchema";

export type OfficeListingTag = {
  key: "viewed" | "unviewed" | "liked" | "disliked" | "visited" | "mine";
  label: string;
  color: "info" | "warning" | "success" | "error" | "primary" | "secondary";
};

export type OfficeMinisiteTotals = {
  listingCount: number;
  openedCount: number;
  likedCount: number;
  dislikedCount: number;
};

export const officeMinisiteTotals = (minisite: REST_schema.CrmOfficeMinisite): OfficeMinisiteTotals => ({
  listingCount: minisite.listingCount,
  openedCount: minisite.listings.filter((listing) => listing.viewCount > 0).length,
  likedCount: minisite.listings.filter((listing) => listing.liked).length,
  dislikedCount: minisite.listings.filter((listing) => listing.disliked).length
});

const listingsLabel = (count: number): string => (count === 1 ? "נכס אחד" : `${count} נכסים`);

/** "5 נכסים · 2 נצפו · 1 אהבו" — the parts that say something; never "0 אהבו". */
export const officeMinisiteSummaryLine = (totals: OfficeMinisiteTotals): string => {
  const parts = [listingsLabel(totals.listingCount)];
  parts.push(totals.openedCount > 0 ? `${totals.openedCount} נצפו` : "טרם נצפו");
  if (totals.likedCount > 0) parts.push(`${totals.likedCount} אהבו`);
  if (totals.dislikedCount > 0) parts.push(`${totals.dislikedCount} לא רלוונטיים`);
  return parts.join(" · ");
};

/**
 * A page the viewer is only told about (`access: "count"` — not an office admin, and not allowed to
 * look through the office's customers): how many listings, and why there is nothing more to open.
 */
export const officeMinisitePresenceLine = (minisite: Pick<REST_schema.CrmOfficeMinisite, "listingCount">): string =>
  `${listingsLabel(minisite.listingCount)} · אין לך הרשאה לצפות בהם`;

/** The section's caption: what was opened is said only when at least one page can be opened. */
export const officeMinisitesCaption = (minisites: readonly Pick<REST_schema.CrmOfficeMinisite, "access">[]): string =>
  minisites.some((minisite) => minisite.access === "full")
    ? "הנכסים שמתווכים אחרים במשרד שלחו ללקוח הזה, ואילו מהם כבר נצפו. לצפייה בלבד."
    : "מתווכים אחרים במשרד כבר שלחו ללקוח הזה נכסים.";

/**
 * What the customer did with one listing on a colleague's page, and whether it is on the viewer's
 * own page too — the duplicate the office asked to be able to see before sending.
 */
export const officeListingTags = (
  listing: REST_schema.CrmOfficeMinisiteListing,
  options: { alsoOnMine: boolean }
): OfficeListingTag[] => {
  const tags: OfficeListingTag[] = [
    listing.viewCount > 0
      ? {
          key: "viewed",
          label: `👁 נצפה${listing.viewCount > 1 ? ` ×${listing.viewCount}` : ""}`,
          color: "info"
        }
      : { key: "unviewed", label: "טרם נצפה", color: "warning" }
  ];
  if (listing.liked) tags.push({ key: "liked", label: "❤ אהבו", color: "success" });
  else if (listing.disliked) tags.push({ key: "disliked", label: "לא רלוונטי", color: "error" });
  if (listing.visited) tags.push({ key: "visited", label: "ביקר בנכס", color: "primary" });
  if (options.alsoOnMine) tags.push({ key: "mine", label: "גם במיניסייט שלך", color: "secondary" });
  return tags;
};

/** The colleague's name: the server's, then the directory's, then the e-mail's local part — never blank. */
export const officeMinisiteRealtorName = (
  minisite: Pick<REST_schema.CrmOfficeMinisite, "realtorName" | "realtorId">,
  directoryName?: string | null
): string => minisite.realtorName || directoryName || minisite.realtorId.split("@")[0] || minisite.realtorId;

/** How many listings a card shows before "show all". */
export const OFFICE_MINISITE_COLLAPSED_ROWS = 4;
